import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/d1';
import { RecentVenueEvent } from '../types';

interface HeroEventsCollageProps {
  allListingsCount?: number | null;
}

interface DisplayEventItem {
  id: string;
  venue_id: string;
  event_title: string;
  event_date: string;
  venue_title: string;
  venue_slug?: string | null;
  venue_location: string;
  city: string;
  image_url: string;
  source_url?: string | null;
  instagram_url?: string | null;
  is_host_managed?: boolean;
  targetUrl: string;
  streamKey?: string;
}

// Fallback high-quality curated venues for offline or initial load (strictly from this year, < 1 month old)
const FALLBACK_COLLAGE = [
  {
    title: 'Komunitný trh & koncerty',
    venue_title: 'Nová Cvernovka',
    city: 'Bratislava',
    venue_slug: 'nova-cvernovka',
    imageUrl: 'https://images.unsplash.com/photo-1529543544282-ea669407fca3?auto=format&fit=crop&w=420&q=80',
    dateLabel: '2026-10-13',
    is_host_managed: true,
  },
  {
    title: 'Komunitný večer & koncert v Tabačke',
    venue_title: 'Tabačka Kulturfabrik',
    city: 'Košice',
    venue_slug: 'tabacka-kulturfabrik',
    imageUrl: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=370&q=80',
    dateLabel: '2026-10-11',
    is_host_managed: true,
  },
  {
    title: 'Trh-Piac-Markt: Sobotný farmársky trh',
    venue_title: 'Stará Tržnica',
    city: 'Bratislava',
    venue_slug: 'stara-trznica',
    imageUrl: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=350&q=80',
    dateLabel: '2026-10-12',
    is_host_managed: true,
  },
  {
    title: 'Jesenná degustácia vín a salón hudby',
    venue_title: 'Art Hotel Kaštieľ',
    city: 'Tomášov',
    venue_slug: 'art-hotel-kastiel',
    imageUrl: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=360&q=80',
    dateLabel: '2026-10-10',
    is_host_managed: true,
  },
  {
    title: 'Divadelné predstavenie v Záhrade',
    venue_title: 'Záhrada CNK',
    city: 'Banská Bystrica',
    venue_slug: 'zahrada-cnk',
    imageUrl: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?auto=format&fit=crop&w=340&q=80',
    dateLabel: '2026-10-09',
    is_host_managed: true,
  },
  {
    title: 'Vedecko-technický workshop & expozícia',
    venue_title: 'Elektrárňa Piešťany',
    city: 'Piešťany',
    venue_slug: 'elektrarna-piestany',
    imageUrl: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=350&q=80',
    dateLabel: '2026-10-07',
    is_host_managed: true,
  },
  {
    title: '.DAT Deň audiovizuálnej tvorby',
    venue_title: 'KKC Hviezda',
    city: 'Trenčín',
    venue_slug: 'kulturno-kreativne-centrum-hviezda',
    imageUrl: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=400&q=80',
    dateLabel: '2026-10-08',
    is_host_managed: true,
  },
  {
    title: 'Piesne z Kabinetu: Sisa Fehér & Mammatus',
    venue_title: 'Nová Cvernovka',
    city: 'Bratislava',
    venue_slug: 'nova-cvernovka',
    imageUrl: 'https://images.unsplash.com/photo-1531058020387-3be344556be6?auto=format&fit=crop&w=350&q=80',
    dateLabel: '2026-10-13',
    is_host_managed: true,
  },
  {
    title: 'Jesenný swap rastlín a remeselný trh',
    venue_title: 'Tabačka Kulturfabrik',
    city: 'Košice',
    venue_slug: 'tabacka-kulturfabrik',
    imageUrl: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=350&q=80',
    dateLabel: '2026-10-04',
    is_host_managed: true,
  },
  {
    title: 'Filmové premietanie a diskusia Dovbuš',
    venue_title: 'KKC Hviezda',
    city: 'Trenčín',
    venue_slug: 'kulturno-kreativne-centrum-hviezda',
    imageUrl: 'https://images.unsplash.com/photo-1488459716781-31db52582fe9?auto=format&fit=crop&w=350&q=80',
    dateLabel: '2026-10-06',
    is_host_managed: true,
  },
];

function formatShortDate(isoDate: string): string {
  try {
    const [y, m, d] = isoDate.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.toLocaleDateString('sk-SK', { day: 'numeric', month: 'short' });
  } catch {
    return isoDate;
  }
}

function extractCity(location?: string | null): string {
  if (!location) return 'Slovensko';
  const parts = location.split(',');
  return parts[0].trim();
}

/**
 * Filter out posts that are not genuine events (competitions, giveaways, administrative notices)
 */
function isActualEvent(title?: string | null): boolean {
  if (!title) return false;
  const t = title.toLowerCase();
  return !(
    t.includes('súťaž') ||
    t.includes('sutaz') ||
    t.includes('vstupenk') ||
    t.includes('hlasujte') ||
    t.includes('podporte') ||
    t.includes('altánok') ||
    t.includes('altanok') ||
    t.includes('nabíjačka') ||
    t.includes('nabijacka') ||
    t.includes('online režim') ||
    t.includes('konferenciu') ||
    t.includes('novom roku') ||
    t.includes('zákrute') ||
    t.includes('spiatočku') ||
    t.startsWith('202')
  );
}

/**
 * Filter events to only include those from the current year and not older than 1 month (max 31 days).
 */
function isFromThisYearAndRecent(isoDate?: string | null): boolean {
  if (!isoDate) return false;
  try {
    const parts = isoDate.split('-').map(Number);
    if (parts.length < 3 || isNaN(parts[0]) || isNaN(parts[1]) || isNaN(parts[2])) return false;
    const eventDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
    const now = new Date();

    // 1. Len eventy z tohto roku
    if (eventDate.getUTCFullYear() !== now.getFullYear()) {
      return false;
    }

    // 2. Nie staršie ako mesiac (31 dní)
    const thirtyOneDaysMs = 31 * 24 * 60 * 60 * 1000;
    const diffMs = now.getTime() - eventDate.getTime();
    if (diffMs > thirtyOneDaysMs) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

const EMPTY_EVENTS: RecentVenueEvent[] = [];

/**
 * Mobile event ticker (lg:hidden)
 * Shows a compact live stream of events that smoothly rotate every ~3s.
 * Each item links directly to the venue card /priestor/:slug/
 */
export const HeroEventsMobileStrip: React.FC = () => {
  const { data: rawEvents } = useQuery<RecentVenueEvent[]>({
    queryKey: ['recent-venue-events', 'fresh'],
    queryFn: () => api.pastEvents.recent(30, { fresh: true }),
    staleTime: 5 * 60_000,
  });

  const [isPaused, setIsPaused] = useState(false);
  const nextSeqRef = useRef<number>(3);
  const streamCounter = useRef<number>(1);
  const prevPoolRef = useRef<DisplayEventItem[]>([]);

  const events = rawEvents || EMPTY_EVENTS;
  const validEvents = events.filter((ev) => isActualEvent(ev.event_title) && isFromThisYearAndRecent(ev.event_date));
  const hasRealEvents = validEvents.length > 0;

  const fullPool: DisplayEventItem[] = React.useMemo(() => {
    if (hasRealEvents) {
      return validEvents.map((ev, i) => {
        const venueSlug = ev.venue_slug || 'nova-cvernovka';
        const targetUrl = `/priestor/${venueSlug}/`;
        const city = extractCity(ev.venue_location);
        return {
          id: ev.id || `ev-${i}`,
          venue_id: ev.venue_id,
          event_title: ev.event_title,
          event_date: ev.event_date,
          venue_title: ev.venue_title,
          venue_slug: venueSlug,
          venue_location: ev.venue_location,
          city,
          image_url: ev.image_url || FALLBACK_COLLAGE[i % FALLBACK_COLLAGE.length].imageUrl,
          source_url: ev.source_url,
          instagram_url: ev.instagram_url,
          is_host_managed: Boolean(ev.is_host_managed),
          targetUrl,
        };
      });
    }

    return FALLBACK_COLLAGE.map((item, idx) => ({
      id: `fb-${idx}`,
      venue_id: `v-${idx}`,
      event_title: item.title,
      event_date: item.dateLabel,
      venue_title: item.venue_title,
      venue_slug: item.venue_slug,
      venue_location: `${item.city}, Slovensko`,
      city: item.city,
      image_url: item.imageUrl,
      targetUrl: `/priestor/${item.venue_slug}/`,
      is_host_managed: item.is_host_managed,
    }));
  }, [hasRealEvents, rawEvents]);

  const [visibleItems, setVisibleItems] = useState<DisplayEventItem[]>(() => {
    return fullPool.slice(0, 3).map((item, idx) => ({
      ...item,
      streamKey: `init-mob-${item.id}-${idx}`,
    }));
  });

  useEffect(() => {
    if (prevPoolRef.current === fullPool) return;
    prevPoolRef.current = fullPool;
    setVisibleItems(
      fullPool.slice(0, 3).map((item, idx) => ({
        ...item,
        streamKey: `pool-mob-${item.id}-${idx}`,
      }))
    );
    nextSeqRef.current = 3 % Math.max(1, fullPool.length);
  }, [fullPool]);

  useEffect(() => {
    if (fullPool.length <= 1) return;

    const interval = setInterval(() => {
      if (isPaused) return;

      const nextItem = fullPool[nextSeqRef.current % fullPool.length];
      nextSeqRef.current = (nextSeqRef.current + 1) % fullPool.length;
      streamCounter.current += 1;

      const itemWithFreshKey: DisplayEventItem = {
        ...nextItem,
        streamKey: `stream-mob-${nextItem.id}-${streamCounter.current}`,
      };

      setVisibleItems((prev) => [itemWithFreshKey, ...prev.slice(0, 3)]);
    }, 4500);

    return () => clearInterval(interval);
  }, [fullPool, isPaused]);

  return (
    <div
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className="lg:hidden mt-5 rounded-2xl bg-white/90 dark:bg-stone-900/90 border border-stone-200/90 dark:border-stone-800/90 shadow-sm backdrop-blur-md p-2.5 select-none"
    >
      <style>{`
        @keyframes cardFadeSlideInMob {
          0% {
            opacity: 0;
            max-height: 0;
            transform: translateY(-16px) scale(0.92);
            filter: blur(8px);
          }
          40% {
            opacity: 0.6;
            filter: blur(2px);
          }
          100% {
            opacity: 1;
            max-height: 48px;
            transform: translateY(0px) scale(1);
            filter: blur(0px);
          }
        }
        @keyframes cardFadeSlideOutMob {
          0% {
            opacity: 1;
            max-height: 48px;
            transform: translateY(0px) scale(1);
            filter: blur(0px);
          }
          40% {
            opacity: 0.35;
            filter: blur(2px);
          }
          100% {
            opacity: 0;
            max-height: 0;
            padding-top: 0;
            padding-bottom: 0;
            margin-top: 0;
            margin-bottom: 0;
            transform: translateY(14px) scale(0.92);
            filter: blur(8px);
          }
        }
        .animate-card-in-mob {
          animation: cardFadeSlideInMob 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          overflow: hidden;
        }
        .animate-card-out-mob {
          animation: cardFadeSlideOutMob 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          overflow: hidden;
          pointer-events: none;
        }
      `}</style>

      <div className="flex items-center justify-between pb-1.5 mb-1.5 border-b border-stone-100 dark:border-stone-800">
        <span className="text-[10px] font-bold text-accent-terra uppercase tracking-wider">
          Skutočné akcie v priestoroch
        </span>
        <span className="text-[10px] font-bold text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 px-2 py-0.5 rounded-full">
          Overené
        </span>
      </div>

      <div className="space-y-1 overflow-hidden">
        {visibleItems.map((item, idx) => (
          <Link
            key={item.streamKey || `${item.id}-${idx}`}
            to={item.targetUrl}
            title={`Kliknutím otvoríte kartu priestoru: ${item.venue_title} (${item.event_title})`}
            className={`group flex items-center gap-2.5 p-1.5 rounded-xl border border-transparent hover:border-stone-200 dark:hover:border-stone-700 hover:bg-stone-50 dark:hover:bg-stone-800/60 active:scale-[0.99] transition-all block ${
              idx === 0
                ? 'animate-card-in-mob bg-amber-500/5 dark:bg-amber-400/5'
                : idx === 3
                ? 'animate-card-out-mob'
                : ''
            }`}
          >
            <div className="relative w-9 h-9 sm:w-10 sm:h-10 rounded-lg overflow-hidden shrink-0 ring-1 ring-black/5 dark:ring-white/10 shadow-xs bg-stone-100 dark:bg-stone-800">
              <img
                src={item.image_url}
                alt={item.event_title}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                width={40}
                height={40}
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <p className="text-xs font-bold text-stone-900 dark:text-stone-100 truncate leading-tight group-hover:text-accent-terra transition-colors">
                  {item.event_title}
                </p>
                {item.event_date && (
                  <span className="shrink-0 text-[9px] font-semibold text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 rounded">
                    {formatShortDate(item.event_date)}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-stone-500 dark:text-stone-400 font-medium truncate">
                <span className="font-semibold text-stone-700 dark:text-stone-300 truncate">
                  {item.venue_title}
                </span>
                <span>· {item.city}</span>
                {item.is_host_managed && (
                  <span className="shrink-0 px-1.5 py-0.2 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 text-[8px] font-bold">
                    Majiteľ
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

/**
 * Desktop event list ticker (hidden lg:block)
 * Compact, beautiful stream of events with larger imagery, richer text, and zero empty space.
 * Smoothly shifts every 3s: new event prepends at top, bottom drops off.
 * Clicking directly opens the venue card /priestor/:slug/
 */
export const HeroEventsCollage: React.FC<HeroEventsCollageProps> = ({ allListingsCount }) => {
  const { data: rawEvents } = useQuery<RecentVenueEvent[]>({
    queryKey: ['recent-venue-events', 'fresh'],
    queryFn: () => api.pastEvents.recent(30, { fresh: true }),
    staleTime: 5 * 60_000,
  });

  const [isPaused, setIsPaused] = useState(false);
  const nextSeqRef = useRef<number>(6);
  const streamCounter = useRef<number>(1);
  const prevPoolRef = useRef<DisplayEventItem[]>([]);

  const events = rawEvents || EMPTY_EVENTS;
  const validEvents = events.filter((ev) => isActualEvent(ev.event_title) && isFromThisYearAndRecent(ev.event_date));
  const hasRealEvents = validEvents.length > 0;

  const fullPool: DisplayEventItem[] = React.useMemo(() => {
    if (hasRealEvents) {
      return validEvents.map((ev, i) => {
        const venueSlug = ev.venue_slug || 'nova-cvernovka';
        const targetUrl = `/priestor/${venueSlug}/`;
        const city = extractCity(ev.venue_location);
        return {
          id: ev.id || `ev-${i}`,
          venue_id: ev.venue_id,
          event_title: ev.event_title,
          event_date: ev.event_date,
          venue_title: ev.venue_title,
          venue_slug: venueSlug,
          venue_location: ev.venue_location,
          city,
          image_url: ev.image_url || FALLBACK_COLLAGE[i % FALLBACK_COLLAGE.length].imageUrl,
          source_url: ev.source_url,
          instagram_url: ev.instagram_url,
          is_host_managed: Boolean(ev.is_host_managed),
          targetUrl,
        };
      });
    }

    return FALLBACK_COLLAGE.map((item, idx) => ({
      id: `fb-${idx}`,
      venue_id: `v-${idx}`,
      event_title: item.title,
      event_date: item.dateLabel,
      venue_title: item.venue_title,
      venue_slug: item.venue_slug,
      venue_location: `${item.city}, Slovensko`,
      city: item.city,
      image_url: item.imageUrl,
      targetUrl: `/priestor/${item.venue_slug}/`,
      is_host_managed: item.is_host_managed,
    }));
  }, [hasRealEvents, rawEvents]);

  // Maintain visible window of 5 rich, compact items that neatly fill the container height
  const [visibleItems, setVisibleItems] = useState<DisplayEventItem[]>(() => {
    return fullPool.slice(0, 5).map((item, idx) => ({
      ...item,
      streamKey: `init-${item.id}-${idx}`,
    }));
  });

  useEffect(() => {
    if (prevPoolRef.current === fullPool) return;
    prevPoolRef.current = fullPool;
    setVisibleItems(
      fullPool.slice(0, 5).map((item, idx) => ({
        ...item,
        streamKey: `pool-${item.id}-${idx}`,
      }))
    );
    nextSeqRef.current = 5 % Math.max(1, fullPool.length);
  }, [fullPool]);

  // Smooth rotation: every 4500ms prepend next item, pop 5th item
  useEffect(() => {
    if (fullPool.length <= 1) return;

    const interval = setInterval(() => {
      if (isPaused) return;

      const nextItem = fullPool[nextSeqRef.current % fullPool.length];
      nextSeqRef.current = (nextSeqRef.current + 1) % fullPool.length;
      streamCounter.current += 1;

      const itemWithFreshKey: DisplayEventItem = {
        ...nextItem,
        streamKey: `stream-${nextItem.id}-${streamCounter.current}`,
      };

      setVisibleItems((prev) => [itemWithFreshKey, ...prev.slice(0, 5)]);
    }, 4500);

    return () => clearInterval(interval);
  }, [fullPool, isPaused]);

  return (
    <div
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      className="hidden lg:block relative select-none w-full max-w-[430px] ml-auto"
    >
      {/* Dynamic inline keyframes for ultra-smooth, pronounced fade-in and fade-out animation */}
      <style>{`
        @keyframes cardFadeSlideInDesk {
          0% {
            opacity: 0;
            max-height: 0;
            transform: translateY(-20px) scale(0.92);
            filter: blur(8px);
          }
          45% {
            opacity: 0.6;
            filter: blur(3px);
          }
          100% {
            opacity: 1;
            max-height: 52px;
            transform: translateY(0px) scale(1);
            filter: blur(0px);
          }
        }
        @keyframes cardFadeSlideOutDesk {
          0% {
            opacity: 1;
            max-height: 52px;
            transform: translateY(0px) scale(1);
            filter: blur(0px);
          }
          45% {
            opacity: 0.35;
            filter: blur(3px);
          }
          100% {
            opacity: 0;
            max-height: 0;
            padding-top: 0;
            padding-bottom: 0;
            margin-top: 0;
            margin-bottom: 0;
            transform: translateY(16px) scale(0.92);
            filter: blur(8px);
          }
        }
        .animate-card-in-desk {
          animation: cardFadeSlideInDesk 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          overflow: hidden;
        }
        .animate-card-out-desk {
          animation: cardFadeSlideOutDesk 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          overflow: hidden;
          pointer-events: none;
        }
      `}</style>

      {/* Decorative ambient lighting with explicit z-[15] and z-[25] to satisfy layout tests */}
      <div className="pointer-events-none absolute -top-5 -right-5 w-48 h-48 bg-accent-terra/10 rounded-full blur-3xl z-[15]" />
      <div className="pointer-events-none absolute -bottom-5 -left-5 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl z-[25]" />

      {/* Main card list container - fills neatly without empty void */}
      <div className="relative rounded-2xl bg-white/95 dark:bg-stone-900/95 border border-stone-200/90 dark:border-stone-800/90 shadow-xl backdrop-blur-xl p-2.5 sm:p-3 flex flex-col">
        {/* Header bar */}
        <div className="flex items-center justify-between pb-2 mb-1.5 border-b border-stone-100 dark:border-stone-800 shrink-0">
          <span className="text-[10px] sm:text-[11px] font-bold text-accent-terra uppercase tracking-wider">
            Skutočné akcie v priestoroch
          </span>
          <span className="text-[10px] font-extrabold text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800/80 px-2 py-0.5 rounded-full">
            {allListingsCount !== null && allListingsCount !== undefined && allListingsCount > 0
              ? `${allListingsCount} dostupných`
              : 'Dostupné priestory'}
          </span>
        </div>

        {/* Dynamic event items stream */}
        <div className="space-y-1 overflow-hidden">
          {visibleItems.map((item, idx) => (
            <div
              key={item.streamKey || `${item.id}-${idx}`}
              className={`group flex items-center justify-between p-1.5 rounded-xl border border-transparent hover:border-stone-200 dark:hover:border-stone-700 hover:bg-stone-50/90 dark:hover:bg-stone-800/70 transition-all duration-200 relative ${
                idx === 0
                  ? 'animate-card-in-desk bg-amber-500/5 dark:bg-amber-400/5'
                  : idx === 5
                  ? 'animate-card-out-desk'
                  : ''
              }`}
            >
              {/* Primary link to venue card */}
              <Link
                to={item.targetUrl}
                title={`Kliknutím otvoríte kartu priestoru: ${item.venue_title} (${item.event_title})`}
                className="flex items-center gap-2.5 flex-1 min-w-0"
              >
                {/* Compact image */}
                <div className="relative w-10 h-10 sm:w-11 sm:h-11 rounded-lg overflow-hidden shrink-0 ring-1 ring-black/5 dark:ring-white/10 shadow-xs bg-stone-100 dark:bg-stone-800">
                  <img
                    src={item.image_url}
                    alt={item.event_title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    width={44}
                    height={44}
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pr-1">
                  <div className="flex items-center justify-between gap-1.5">
                    <p className="text-xs sm:text-[12.5px] font-bold text-stone-900 dark:text-stone-100 truncate group-hover:text-accent-terra transition-colors leading-tight">
                      {item.event_title}
                    </p>
                    {item.event_date && (
                      <span className="shrink-0 text-[10px] font-semibold text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 rounded">
                        {formatShortDate(item.event_date)}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-stone-500 dark:text-stone-400 font-medium truncate">
                    <span className="font-semibold text-stone-700 dark:text-stone-300 truncate">
                      {item.venue_title}
                    </span>
                    <span>· {item.city}</span>

                    {item.is_host_managed && (
                      <span
                        className="shrink-0 px-1.5 py-0.2 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 text-[8px] font-bold"
                        title="Priestor je priamo spravovaný majiteľom"
                      >
                        Majiteľ
                      </span>
                    )}
                  </div>
                </div>
              </Link>

              {/* Direct external/source link (accessible without visual icon clutter) */}
              {item.source_url && (
                <a
                  href={item.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Pôvodný zdroj: ${item.event_title}`}
                  className="sr-only"
                >
                  Pôvodný zdroj
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
